import { useState } from "react";

/**
 * `startNode`/`endNode` are controlled here (not local to the dialog) because picking an
 * endpoint on the map closes the dialog, waits for a click, then reopens it — the endpoint
 * not currently being repicked must survive that round-trip.
 */
export function useLongitudinalProfile() {
  const [open, setOpen] = useState(false);
  const [startNode, setStartNode] = useState<string | null>(null);
  const [endNode, setEndNode] = useState<string | null>(null);
  const [picking, setPicking] = useState<"start" | "end" | null>(null);

  /** Seeds both endpoints from `nodes` only the first time this opens; after that whatever was last picked sticks. */
  function openWithDefaults(nodes: readonly { name: string }[]) {
    if (startNode === null) setStartNode(nodes[0]?.name ?? null);
    if (endNode === null) setEndNode(nodes[nodes.length - 1]?.name ?? null);
    setOpen(true);
  }

  function requestPick(which: "start" | "end") {
    setOpen(false);
    setPicking(which);
  }

  /** Returns `false` (and changes nothing) when not currently picking, so the caller falls through to its own normal click handling. */
  function resolvePick(nodeName: string): boolean {
    if (!picking) return false;
    if (picking === "start") setStartNode(nodeName);
    else setEndNode(nodeName);
    setPicking(null);
    setOpen(true);
    return true;
  }

  function cancelPick() {
    setPicking(null);
    setOpen(true);
  }

  function close() {
    setOpen(false);
  }

  function reset() {
    setOpen(false);
    setPicking(null);
    setStartNode(null);
    setEndNode(null);
  }

  return {
    open,
    startNode,
    endNode,
    picking,
    setStartNode,
    setEndNode,
    openWithDefaults,
    requestPick,
    resolvePick,
    cancelPick,
    close,
    reset,
  };
}
